/**
 * imageSizing — Unsplash query helpers (PR Perf-M1/M2).
 *
 * Stock photos come from Unsplash and default to ~2000px wide originals.
 * That's wasted bandwidth for mobile players, thumbnails and admin grids.
 * `sizedUnsplashUrl` rewrites the `w` / `q` / `auto` query params; non-
 * Unsplash URLs (e.g. user uploads on Supabase Storage) are returned as-is.
 */

const UNSPLASH_HOSTS = ["images.unsplash.com", "unsplash.com"]

const isUnsplash = (url: string): boolean => {
  try {
    const host = new URL(url).hostname
    return UNSPLASH_HOSTS.some(h => host === h || host.endsWith(`.${h}`))
  } catch {
    return false
  }
}

/**
 * Append/override Unsplash sizing query parameters. Returns the URL
 * unchanged for non-Unsplash sources.
 *
 * @param url      raw photo URL
 * @param width    target rendered width (CSS px). For retina, callers may
 *                 want to pass 2× the displayed size.
 * @param quality  JPEG quality (default 70 — visually indistinguishable
 *                 from 85 at typical viewport sizes, ~30% smaller payload).
 */
export const sizedUnsplashUrl = (
  url: string | undefined | null,
  width: number,
  quality = 70
): string => {
  if (!url) return ""
  if (!isUnsplash(url)) return url
  try {
    const u = new URL(url)
    u.searchParams.set("w", String(width))
    u.searchParams.set("q", String(quality))
    if (!u.searchParams.has("auto")) {
      u.searchParams.set("auto", "format")
    }
    return u.toString()
  } catch {
    return url
  }
}
